import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { useState } from 'react';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { DemosScore } from '../../../components/LeaderboardComponents/DemosScore';
import { TabToggle } from '../../../../web/components-v2/TabToggle';
import { ILeaderboardDemosFilter, LeaderboardDemosGroupBy } from '../../../queries/Leaderboard/criteria';
import { ILeaderboardDemos } from '../../../viewModels/gamification';
import { useQuotaTeamAutocompleteQuery } from '../../../queries/Aida/useQuotaTeamAutocomplete';
import { useLeaderboardDemosQuery } from '../../../queries/Leaderboard';
import { styleSheet } from '../styles';
import { DailySlider } from './DailySlider';
import { EmptyState } from './EmptyState';
import { OfficeSelect } from './OfficeSelect';

const GROUP_BY_ITEMS: { label: string; value: LeaderboardDemosGroupBy }[] = [
	{ label: 'Vertical', value: 'Vertical' },
	{ label: 'Quota team', value: 'QuotaTeam' },
];

const isGroupBy = (value: string): value is LeaderboardDemosGroupBy =>
	GROUP_BY_ITEMS.some(item => item.value === value);

// The endpoint answers an empty day with zeroed totals and no rows rather than no data.
const hasDemoRows = (data: ILeaderboardDemos | null | undefined) =>
	Object.keys(data?.totalVerticalLeaderboard?.verticalStatistics ?? {}).length > 0 ||
	Object.keys(data?.roleLeaderboards ?? {}).length > 0;

export const Demos: React.FC = () => {
	const userSession = useUserSession();
	const isSalesCoffeeAccount = !!userSession?.account?.isLevitateSalesCoffeeAccount;
	const { data: quotaTeams, isPending: isQuotaTeamsPending } = useQuotaTeamAutocompleteQuery({
		enabled: isSalesCoffeeAccount,
	});
	const hasQuotaTeams = isSalesCoffeeAccount && !isQuotaTeamsPending && (quotaTeams?.length ?? 0) > 0;

	const [filter, setFilter] = useState<ILeaderboardDemosFilter>({
		day: moment().format('YYYY-MM-DD'),
	});

	// Effective groupBy: user's explicit choice, or the data-driven default once autocomplete resolves.
	// Non-Sales-Coffee accounts skip the autocomplete entirely and default to Vertical immediately.
	const groupBy: LeaderboardDemosGroupBy | undefined =
		filter.groupBy ??
		(!isSalesCoffeeAccount ? 'Vertical' : isQuotaTeamsPending ? undefined : hasQuotaTeams ? 'QuotaTeam' : 'Vertical');

	const { data, isError, isPending } = useLeaderboardDemosQuery({ filter, groupBy });

	const handleDateChange = (value: string) => {
		setFilter(prev => ({ ...prev, day: value }));
	};

	const handleGroupByChange = (next: string) => {
		if (next === groupBy || !isGroupBy(next)) {
			return;
		}
		setFilter(prev => ({ ...prev, groupBy: next }));
	};

	const handleOfficeChange = (officeLocation: string | null) => {
		setFilter(prev => ({ ...prev, officeLocation: officeLocation ?? undefined }));
	};

	const renderFilter = (
		<>
			<DailySlider onChange={handleDateChange} />
			<div className={css(styleSheet.demosControls)}>
				{hasQuotaTeams && groupBy ? (
					<TabToggle
						aria-label='Group demos by'
						items={GROUP_BY_ITEMS}
						value={groupBy}
						onValueChange={handleGroupByChange}
					/>
				) : null}
				<OfficeSelect
					selected={filter.officeLocation ?? null}
					onChange={handleOfficeChange}
					styles={[styleSheet.officeSelect]}
				/>
			</div>
		</>
	);

	const groupLabel = groupBy === 'QuotaTeam' ? 'Quota Teams' : 'Verticals';

	if (isPending) {
		return <LoadingSpinner />;
	}

	const emptyState =
		isError && !data ? (
			<EmptyState title="Couldn't load demos." description='It will try again shortly.' />
		) : !hasDemoRows(data) ? (
			<EmptyState title='No data yet.' />
		) : null;

	return <DemosScore groupLabel={groupLabel} renderEmptyState={emptyState} renderFilter={renderFilter} scores={data} />;
};
